import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// Components
import { Row, Col, Dropdown, Button, OverlayTrigger } from "react-bootstrap";
import CustomColorPicker from "./CustomColorPicker";
import { DEFAULT_SELECTION, CIRCLE_COLORS } from "../constants";
import { TextStyleSelection } from "./TextStyleSelection";
import { CirclePicker } from "react-color";

// Action
import { createDefaultMenuSetting } from "../actions/Settings";
import { createDefaultRestaurantSetting } from "../actions/Menu";
import ADAPopover from "../Components/layout/ADAPopover";
import SearchbarDropdown from "./SearchbarDropdown";

const MENU_TEXT_SETTINGS = [
  {
    text: "Header 1",
    allowSelectColor: true,
    key: "headerOne",
    defaultColor: DEFAULT_SELECTION.quaternary,
  },
  {
    text: "Header 2",
    allowSelectColor: true,
    key: "headerTwo",
  },
  {
    text: "Header 3",
    allowSelectColor: true,
    key: "headerThree",
  },
  {
    text: "Header 4",
    allowSelectColor: true,
    key: "headerFour",
  },
  {
    text: "Normal Text",
    allowSelectColor: true,
    key: "normalText",
  },
  {
    text: "Subtext",
    allowSelectColor: true,
    key: "subtext",
    defaultColor: DEFAULT_SELECTION.tertiaryColor,
  },
];

const RESTAURANT_TEXT_SETTING = [
  {
    text: "Restaurant Name",
    allowSelectColor: true,
    key: "restaurantName",
    defaultColor: DEFAULT_SELECTION.secondaryColor,
  },
  {
    text: "Introduction Text",
    allowSelectColor: true,
    key: "descriptionText",
    defaultColor: DEFAULT_SELECTION.secondaryColor,
  },
  {
    text: "Date",
    allowSelectColor: true,
    key: "dateText",
    defaultColor: DEFAULT_SELECTION.secondaryColor,
  },
];

/**
 * Detect is color input is white-color or not
 * @param {string} color need to compare
 */
// const isWhileColor = (color) => {
//   const compareColorStr = color ? color.toLowerCase() : "#fff";
//   return (
//     compareColorStr === "#fff" ||
//     compareColorStr === "#ffffff" ||
//     compareColorStr === "rgb(255, 255, 255)"
//   );
// };

export const FontSelectionItem = ({
  item,
  settingObj,
  handleUpdateSetting,
}) => {
  const { text, allowSelectColor, key, defaultColor } = item;
  const [color, setColor] = useState(
    defaultColor || DEFAULT_SELECTION.primaryColor
  );
  const [fontStyle, setFontStyle] = useState([]);
  const [font, setFont] = useState();

  useEffect(() => {
    setFont(settingObj.font || DEFAULT_SELECTION.font);
  }, [settingObj.font]);

  useEffect(() => {
    setColor(
      settingObj.color || defaultColor || DEFAULT_SELECTION.primaryColor
    );
  }, [settingObj.color, defaultColor]);

  useEffect(() => {
    setFontStyle(settingObj.textstyle || []);
  }, [settingObj.textstyle]);

  /**
   * Change font-family for text
   * @param {string} hex
   */
  const handleChangeFont = (item) => {
    const selectedFont = item || {};
    setFont(selectedFont.font);
    handleUpdateSetting({
      [key]: {
        ...settingObj,
        ...selectedFont,
      },
    });
  };

  /**
   * Change color for text
   * @param {string} hex
   */
  const handleChangeColor = ({ hex }) => {
    setColor(hex);
    handleUpdateSetting({
      [key]: {
        ...settingObj,
        color: hex,
      },
    });
  };

  /**
   * Change font style for text
   * @param {Event} event
   */
  const handleChangeFontStyle = (event) => {
    let textstyle = fontStyle;
    switch (event.target.innerText) {
      case "B":
        // Check to add/remove font style class name if it is not exist/exist
        textstyle = textstyle.includes("bold")
          ? textstyle.filter((item) => item !== "bold")
          : textstyle.concat(["bold"]);
        break;

      case "i":
        // Check to add/remove font style class name if it is not exist/exist
        textstyle = textstyle.includes("italic")
          ? textstyle.filter((item) => item !== "italic")
          : textstyle.concat(["italic"]);
        break;

      default:
        break;
    }

    setFontStyle(textstyle);
    handleUpdateSetting({
      [key]: {
        ...settingObj,
        textstyle,
      },
    });
  };

  const resetConfigStyle = () => {
    const color = defaultColor || DEFAULT_SELECTION.primaryColor;

    setColor(color);
    setFont(DEFAULT_SELECTION.font);
    setFontStyle([]);

    handleUpdateSetting({
      [key]: {
        font: DEFAULT_SELECTION.font,
        textstyle: [],
        color,
      },
    });
  };

  return (
    <>
      <Row className="font-selection-item mx-0">{text} :</Row>
      <Row>
        <Col className="font-selection-item-header" xs="4">
          Typeface
        </Col>
        <Col className="font-selection-item-header" xs="8">
          Color
        </Col>
      </Row>

      <Row className="align-items-center font-selection-item-setting">
        <Col className="text-muted mt-1" lg="4" sm="3" xs="4">
          <SearchbarDropdown
            handleChangeFont={handleChangeFont}
            font={font}
            keyTest={key}
          />
        </Col>
        <Col className="text-muted mt-1" lg="3" xl="4" sm="3" xs="2">
          {allowSelectColor && (
            <Dropdown
              className="d-flex align-items-center"
              data-testid={`color-picker-${key}`}
            >
              <CirclePicker
                width="87%"
                colors={CIRCLE_COLORS}
                circleSize={23}
                circleSpacing={6}
                color={color}
                onChangeComplete={handleChangeColor}
                className="d-none d-sm-flex"
              />
              <Dropdown.Toggle
                data-testid={`select-color-${key}`}
                id="dropdown-color"
                className="dropdown-color d-flex align-items-center justify-content-center p-1"
                variant="outline-dark"
              />

              <Dropdown.Menu className="p-0">
                <CustomColorPicker
                  color={color}
                  handleChangeColor={handleChangeColor}
                />
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Col>
        <Col className="text-muted mt-1" lg="3" xl="2" sm="3" xs="3">
          <TextStyleSelection
            activeBold={fontStyle.includes("bold")}
            activeItalic={fontStyle.includes("italic")}
            handleChangeFontStyle={handleChangeFontStyle}
            textName={key}
          />
        </Col>
        <Col className="mt-1" lg="2" sm="3" xs="3">
          <Row>
            <Button
              variant="outline-dark"
              onClick={resetConfigStyle}
              className="reset"
            >
              Reset
            </Button>
          </Row>
        </Col>
      </Row>
      <hr />
    </>
  );
};

export const FontSelection = ({
  setting,
  handleUpdateSetting,
  isMenuSetting = false,
}) => {
  const textTypes = isMenuSetting
    ? MENU_TEXT_SETTINGS
    : RESTAURANT_TEXT_SETTING;
  const defaultSetting = isMenuSetting
    ? createDefaultMenuSetting()
    : createDefaultRestaurantSetting();

  return (
    // Disabled ADA Popup
    <OverlayTrigger
      placement="top"
      delay={{ show: 250, hide: 400 }}
      overlay={ADAPopover}
      transition={false}
      animation={false}
    >
      <div className="mt-2">
        {textTypes.map((item) => {
          const settingObj =
            setting && setting[item.key]
              ? setting[item.key]
              : defaultSetting[item.key];

          return (
            <FontSelectionItem
              item={item}
              key={item.key}
              settingObj={settingObj}
              handleUpdateSetting={handleUpdateSetting}
            />
          );
        })}
      </div>
    </OverlayTrigger>
  );
};

FontSelectionItem.propTypes = {
  item: PropTypes.shape({
    text: PropTypes.string,
    allowSelectColor: PropTypes.bool,
    key: PropTypes.string,
    defaultColor: PropTypes.string,
  }),
  settingObj: PropTypes.shape({
    font: PropTypes.string,
    color: PropTypes.string,
    textstyle: PropTypes.array,
  }),
  handleUpdateSetting: PropTypes.func.isRequired,
};

FontSelection.propTypes = {
  setting: PropTypes.object.isRequired,
  handleUpdateSetting: PropTypes.func.isRequired,
  isMenuSetting: PropTypes.bool,
};

export default FontSelection;
